import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';

/** A small curated emoji set for the chats (user request 2026-09-27). */
const EMOJIS = [
  '🙂', '😀', '😄', '😂', '🤣', '😊', '😉', '😎',
  '🤔', '😮', '😅', '😬', '😢', '😭', '😡', '🤯',
  '👍', '👎', '👏', '🙌', '🙏', '💪', '🤝', '👋',
  '🎉', '🏆', '🥇', '🔥', '⭐', '💯', '❤️', '💙',
  '⚓', '🚢', '💥', '🌊', '🎯', '🧠', '🍀', '🎲',
  '⚫', '⚪', '🃏', '🕐', '☕', '🍕', '✅', '❌',
];

/** Emoji button + popover; the chosen emoji is inserted at the input's cursor. */
export function EmojiPicker(props: { onPick: (emoji: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div class="emoji-picker" ref={ref}>
      <button
        type="button"
        class="btn btn-ghost btn-icon emoji-toggle"
        onClick={() => setOpen(!open)}
        disabled={props.disabled}
        aria-label={t.chatUi.emojis}
        title={t.chatUi.emojis}
        aria-expanded={open}
      >
        🙂
      </button>
      {open && (
        <div class="emoji-grid" role="listbox" aria-label={t.chatUi.emojis}>
          {EMOJIS.map((e) => (
            <button type="button" class="emoji" onClick={() => props.onPick(e)}>
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Inserts text at the cursor of an input and returns the new value. */
export function insertAtCursor(input: HTMLInputElement | null, value: string, insert: string): string {
  if (!input) return value + insert;
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? value.length;
  const next = value.slice(0, start) + insert + value.slice(end);
  // Focus right away (Enter then sends); the cursor is placed after the re-render.
  // setTimeout instead of requestAnimationFrame, which does not run in background tabs.
  input.focus();
  setTimeout(() => input.setSelectionRange(start + insert.length, start + insert.length), 0);
  return next;
}
